import { useState, useCallback, useEffect, useRef, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  resolveBathImage,
  fallbackForBrokenBathImage,
} from "@/lib/bathImageMap";

export interface GalleryImage {
  src: string;
  alt: string;
  caption?: string;
  title?: string;
  description?: string;
}

interface SwipeableGalleryProps {
  images: GalleryImage[];
  className?: string;
}

/**
 * Swipeable image gallery with thumbnails, arrow nav, and full-screen lightbox.
 * Mobile: drag/swipe via Embla. Desktop: arrows + click-to-zoom.
 */
export const SwipeableGallery = ({ images: rawImages, className }: SwipeableGalleryProps) => {
  // Rewrite any legacy/alternate paths to canonical asset URLs before render.
  const images = rawImages.map((img) => {
    const src = resolveBathImage(img.src);
    return { ...img, src: src.includes("/images/bath/") ? `${src}?v=bath-aligned-20260503` : src };
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const [zoomed, setZoomed] = useState(false);
  const [zoomOrigin, setZoomOrigin] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const ZOOM_SCALE = 2.5;
  const dragRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);
  const lastTapRef = useRef<{ time: number; x: number; y: number }>({ time: 0, x: 0, y: 0 });

  const resetZoom = () => {
    setZoomed(false);
    setPan({ x: 0, y: 0 });
  };

  const openLightbox = (i: number) => {
    setLightboxIndex(i);
    resetZoom();
    setLightboxOpen(true);
  };
  const lbPrev = () => {
    resetZoom();
    setLightboxIndex((i) => (i - 1 + images.length) % images.length);
  };
  const lbNext = () => {
    resetZoom();
    setLightboxIndex((i) => (i + 1) % images.length);
  };

  const clampPan = (px: number, py: number, rect: DOMRect) => {
    // When scaled, the image extends (scale-1)*size beyond original bounds.
    // Allowable pan is half of that overflow in each direction.
    const maxX = (rect.width * (ZOOM_SCALE - 1)) / 2;
    const maxY = (rect.height * (ZOOM_SCALE - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, px)),
      y: Math.max(-maxY, Math.min(maxY, py)),
    };
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLImageElement>) => {
    if (!zoomed) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: pan.x,
      originY: pan.y,
      moved: false,
      pointerId: e.pointerId,
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLImageElement>) => {
    const d = dragRef.current;
    if (!d?.active || !zoomed) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    const rect = e.currentTarget.getBoundingClientRect();
    setPan(clampPan(d.originX + dx, d.originY + dy, rect));
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLImageElement>) => {
    const d = dragRef.current;
    if (d?.active) {
      try { e.currentTarget.releasePointerCapture(d.pointerId); } catch {}
      const wasDrag = d.moved;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      dragRef.current = null;
      // Swipe-down gesture (touch) to reset zoom
      if (zoomed && e.pointerType === "touch" && dy > 80 && Math.abs(dy) > Math.abs(dx) * 1.5) {
        resetZoom();
        return;
      }
      if (wasDrag) return; // suppress click-to-toggle when panning
    }
    // Double-tap detection (touch only) for quick zoom toggle on mobile
    if (e.pointerType === "touch") {
      const now = Date.now();
      const last = lastTapRef.current;
      const dt = now - last.time;
      const dist = Math.hypot(e.clientX - last.x, e.clientY - last.y);
      if (dt < 300 && dist < 30) {
        e.preventDefault();
        if (zoomed) {
          resetZoom();
        } else {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          setZoomOrigin({ x, y });
          setPan({ x: 0, y: 0 });
          setZoomed(true);
        }
        lastTapRef.current = { time: 0, x: 0, y: 0 };
        return;
      }
      lastTapRef.current = { time: now, x: e.clientX, y: e.clientY };
    }
  };

  const handleZoomClick = (e: ReactMouseEvent<HTMLImageElement>) => {
    if (zoomed) {
      // Click without drag while zoomed -> zoom out
      resetZoom();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin({ x, y });
    setPan({ x: 0, y: 0 });
    setZoomed(true);
  };

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") lbPrev();
      if (e.key === "ArrowRight") lbNext();
      if (e.key === "Escape") resetZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, images.length]);

  return (
    <div className={cn("relative", className)}>
      {/* Carousel */}
      <div className="relative overflow-hidden border border-neutral-200 bg-white" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {images.map((img, i) => (
            <figure
              key={img.src}
              className="relative m-0 min-w-0 flex-[0_0_100%]"
              itemScope
              itemType="https://schema.org/ImageObject"
            >
              <meta itemProp="contentUrl" content={img.src} />
              <meta itemProp="name" content={img.title || img.alt} />
              {img.description && <meta itemProp="description" content={img.description} />}
              <button
                type="button"
                onClick={() => openLightbox(i)}
                className="group block w-full"
                aria-label={`Open ${img.alt} in full screen`}
              >
                <div className="relative aspect-square bg-neutral-50 sm:aspect-[4/3] lg:aspect-[4/3]">
                  <img
                    src={img.src}
                    alt={img.alt}
                    title={img.title || img.alt}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                    itemProp="url"
                    onError={(e) => {
                      const el = e.currentTarget as HTMLImageElement;
                      const fb = fallbackForBrokenBathImage(el.src);
                      if (fb && !el.dataset.fallbackApplied) {
                        el.dataset.fallbackApplied = "1";
                        el.src = fb;
                      }
                    }}
                    className="absolute inset-0 h-full w-full select-none object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  <div className="absolute right-3 top-3 rounded-full bg-white/80 p-2 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100">
                    <ZoomIn className="h-4 w-4 text-neutral-900" />
                  </div>
                  {img.caption && (
                    <figcaption
                      itemProp="caption"
                      className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                        {img.caption}
                      </span>
                    </figcaption>
                  )}
                </div>
              </button>
            </figure>
          ))}
        </div>

        {/* Arrows */}
        <button
          type="button"
          onClick={scrollPrev}
          aria-label="Previous image"
          className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-neutral-200 bg-white/90 p-2 text-neutral-900 shadow-md backdrop-blur-sm transition hover:bg-white sm:block"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={scrollNext}
          aria-label="Next image"
          className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full border border-neutral-200 bg-white/90 p-2 text-neutral-900 shadow-md backdrop-blur-sm transition hover:bg-white sm:block"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Dot counter (mobile) */}
        <div className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-white/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-900 backdrop-blur-sm sm:hidden">
          {selectedIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="mt-4 grid grid-cols-5 gap-2 sm:gap-3">
        {images.map((img, i) => (
          <button
            key={`thumb-${img.src}`}
            type="button"
            onClick={() => scrollTo(i)}
            aria-label={`Show image ${i + 1}`}
            className={cn(
              "relative aspect-square overflow-hidden bg-neutral-100 transition",
              selectedIndex === i
                ? "ring-2 ring-amber-500"
                : "border border-neutral-200 opacity-70 hover:opacity-100"
            )}
          >
            <img
              src={img.src}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="h-[95vh] max-w-[95vw] border-0 bg-black/95 p-0">
          <div className="relative flex h-full w-full items-center justify-center">
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close"
                className="absolute right-3 top-3 z-50 h-11 w-11 rounded-full bg-white/10 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>

            <button
              type="button"
              onClick={lbPrev}
              aria-label="Previous"
              className="absolute left-3 top-1/2 z-40 -translate-y-1/2 rounded-full border border-amber-500/40 bg-black/60 p-3 text-amber-200 hover:bg-black/80"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={lbNext}
              aria-label="Next"
              className="absolute right-3 top-1/2 z-40 -translate-y-1/2 rounded-full border border-amber-500/40 bg-black/60 p-3 text-amber-200 hover:bg-black/80"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <div
              className="flex h-full w-full items-center justify-center overflow-hidden"
              onClick={(e) => {
                if (e.target === e.currentTarget && zoomed) resetZoom();
              }}
            >
              <img loading="lazy" decoding="async"
                key={images[lightboxIndex]?.src}
                src={images[lightboxIndex]?.src}
                alt={images[lightboxIndex]?.alt}
                onClick={handleZoomClick}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                style={{
                  transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                  transform: zoomed
                    ? `translate(${pan.x}px, ${pan.y}px) scale(${ZOOM_SCALE})`
                    : "translate(0,0) scale(1)",
                  cursor: zoomed ? (dragRef.current?.active ? "grabbing" : "grab") : "zoom-in",
                  touchAction: zoomed ? "none" : "auto",
                }}
                className={cn(
                  "max-h-full max-w-full select-none object-contain ease-out",
                  dragRef.current?.active ? "" : "transition-transform duration-300"
                )}
                draggable={false}
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  const fb = fallbackForBrokenBathImage(el.src);
                  if (fb && !el.dataset.fallbackApplied) {
                    el.dataset.fallbackApplied = "1";
                    el.src = fb;
                  }
                }}
              />
            </div>

            {/* Zoom toggle button */}
            <button
              type="button"
              onClick={() => (zoomed ? resetZoom() : setZoomed(true))}
              aria-label={zoomed ? "Zoom out" : "Zoom in"}
              className="absolute right-16 top-3 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              {zoomed ? <ZoomOut className="h-5 w-5" /> : <ZoomIn className="h-5 w-5" />}
            </button>

            <div className="pointer-events-none absolute inset-x-0 bottom-4 text-center">
              <p className="inline-block rounded-full bg-black/60 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-amber-200 backdrop-blur-sm">
                {lightboxIndex + 1} / {images.length}
                {images[lightboxIndex]?.caption ? ` · ${images[lightboxIndex].caption}` : ""}
                <span className="ml-2 hidden sm:inline opacity-70">· Click to zoom · Drag to pan</span>
                <span className="ml-2 inline sm:hidden opacity-70">· Double-tap to zoom · Swipe down to reset</span>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SwipeableGallery;
